import { useEffect } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { Check, XClose } from "@untitledui/icons"

import type { ApiEstimateImage } from "@/lib/api/types"
import { componentLabel, sideLabel } from "@/lib/estimates/catalog"
import { cx } from "@/lib/utils/cx"
import type { CanvasDispatch, CanvasLine } from "./use-canvas"

// From the part: which photos show it. Every photo as a tile; tap to
// tick. Same tick as the list, seen from the other end.

export function PhotoPicker({
  line,
  images,
  dispatch,
  onClose,
}: {
  line: CanvasLine | null
  images: ApiEstimateImage[]
  dispatch: CanvasDispatch
  onClose: () => void
}) {
  const reduce = useReducedMotion()
  const open = !!line

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose() }
    }
    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
  }, [open, onClose])

  const count = line ? line.photos.filter((p) => images.some((img) => img.id === p.imageId)).length : 0
  const title = line ? `Which photos show the ${componentLabel(line.component).toLowerCase()}?` : ""

  return (
    <AnimatePresence>
      {line && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/30 p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(12px) scale(0.98)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(8px) scale(0.98)" }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[88dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl ring-1 ring-zinc-900/10 sm:max-w-[640px] sm:rounded-2xl"
          >
            <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
              <div className="min-w-0">
                <h2 className="text-[16px] font-semibold text-zinc-900">{title}</h2>
                <p className="mt-0.5 text-[12.5px] text-zinc-500">
                  Tap a photo to tick it · {count} of {images.length} ticked
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="grid size-9 shrink-0 place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30"
                aria-label="Close"
              >
                <XClose className="size-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {images.map((img, i) => {
                  const on = line.photos.some((p) => p.imageId === img.id)
                  return (
                    <li key={img.id}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={`Photo ${i + 1}, ${sideLabel(img.side)}`}
                        onClick={() => dispatch({ t: "toggle", key: line.key, imageId: img.id })}
                        className={cx(
                          "group relative block w-full overflow-hidden rounded-lg bg-zinc-100 text-left transition-[box-shadow] duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/50",
                          on ? "ring-2 ring-zinc-900" : "ring-1 ring-zinc-200 hover:ring-zinc-400",
                        )}
                      >
                        <span className="block aspect-[4/3]">
                          <img src={img.src} alt="" className={cx("size-full object-cover transition-opacity duration-150", !on && "opacity-80 group-hover:opacity-100")} draggable={false} />
                        </span>
                        <span
                          aria-hidden
                          className={cx(
                            "absolute right-2 top-2 grid size-7 place-items-center rounded-full transition-colors duration-150",
                            on ? "bg-zinc-900 text-white" : "bg-white/90 text-transparent ring-1 ring-zinc-900/20",
                          )}
                        >
                          <Check className="size-4" strokeWidth={3} />
                        </span>
                        <span className="block px-2.5 py-1.5 text-[12px] text-zinc-700">
                          <span className="font-semibold tabular-nums text-zinc-900">{i + 1}</span> · {sideLabel(img.side)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>

            <div className="flex justify-end border-t border-zinc-100 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-lg bg-zinc-900 px-5 text-[13.5px] font-semibold text-white hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2"
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
